import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** Margen mínimo al calcular altura expandida (evita recorte por subpíxeles). */
const EXPAND_HEIGHT_BUFFER = 2;

/** Por debajo de esta altura estimada, la tarjeta completa no cabe sin expandir. */
const FULL_CARD_MIN_HEIGHT = 132;

const getEventShell = (node) => node?.closest?.('.rbc-event') ?? null;

const measureNaturalCardHeight = (card) => {
    const hadExpandedClass = card.classList.contains('cal-event-card--expanded');
    card.classList.add('cal-event-card--expanded');

    const naturalHeight = Math.ceil(card.scrollHeight);

    if (!hadExpandedClass) {
        card.classList.remove('cal-event-card--expanded');
    }

    return naturalHeight;
};

const isCardContentClipped = (card) => {
    if (!card) return false;

    const eventEl = getEventShell(card);
    if (!eventEl) return false;

    if (card.scrollHeight > card.clientHeight + 2) return true;

    const slotHeight = eventEl.offsetHeight;
    const naturalHeight = measureNaturalCardHeight(card);

    if (naturalHeight > slotHeight + 2) return true;

    const title = card.querySelector('.cal-event-card__title');
    const titleClamped = title && title.scrollHeight > title.clientHeight + 2;

    const hiddenSections = [
        '.cal-event-card__bottom',
        '.cal-event-card__tags',
        '.cal-event-card__dates',
        '.cal-event-card__progress-block',
    ].some((selector) => {
        const element = card.querySelector(selector);
        return element && window.getComputedStyle(element).display === 'none';
    });

    return titleClamped || hiddenSections || slotHeight < FULL_CARD_MIN_HEIGHT;
};

/**
 * Expande tarjetas de evento en semana/día cuando el contenido no cabe,
 * restaurando la altura inline de React Big Calendar al colapsar.
 */
export const useCalendarEventExpand = () => {
    const cardRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const pendingExpandRef = useRef(false);
    const savedShellStyleRef = useRef(null);

    const collapseExpanded = useCallback(() => {
        const eventEl = getEventShell(cardRef.current);
        pendingExpandRef.current = false;

        if (eventEl) {
            eventEl.classList.remove('cal-event--expanded');

            if (savedShellStyleRef.current) {
                eventEl.style.height = savedShellStyleRef.current.height;
                eventEl.style.minHeight = savedShellStyleRef.current.minHeight;
                savedShellStyleRef.current = null;
            }
        }

        setExpanded(false);
    }, []);

    const expandCard = useCallback(() => {
        const card = cardRef.current;
        const eventEl = getEventShell(card);
        if (!card || !eventEl || expanded || pendingExpandRef.current) return;
        if (!isCardContentClipped(card)) return;

        if (!savedShellStyleRef.current) {
            savedShellStyleRef.current = {
                height: eventEl.style.height,
                minHeight: eventEl.style.minHeight,
            };
        }

        pendingExpandRef.current = true;
        setExpanded(true);
    }, [expanded]);

    useLayoutEffect(() => {
        if (!expanded || !pendingExpandRef.current) return;

        const card = cardRef.current;
        const eventEl = getEventShell(card);
        if (!card || !eventEl) {
            pendingExpandRef.current = false;
            return;
        }

        eventEl.classList.add('cal-event--expanded');

        const slotHeight = eventEl.offsetHeight;
        const fullHeight = measureNaturalCardHeight(card) + EXPAND_HEIGHT_BUFFER;

        eventEl.style.height = `${Math.max(slotHeight, fullHeight)}px`;
        pendingExpandRef.current = false;
    }, [expanded]);

    return {
        cardRef,
        expanded,
        expandCard,
        collapseExpanded,
    };
};
